import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from '../../common.js';
import { HOUSEHOLD_DOC } from '../household/index.js';
/**
 * Daily digest module (ROADMAP §2 "Pulled forward from Phase 2", WS-14).
 *
 * The digest is a **pure read** over the `calendar`, `lists`/`list:<id>`, `checkins` and
 * `household` docs — it owns no Yjs doc of its own and never writes collaborative state.
 * The one thing it *does* need to persist is each member's preferred send time, and the
 * natural home for that (a household member preference) is the `household` doc WS-2
 * already owns — so this module contributes one more key inside it, the same way WS-4's
 * calendar module reads `HOUSEHOLD_MEMBERS_KEY`/`HOUSEHOLD_BIRTHDAYS_KEY` without owning
 * `household`'s directory.
 */
/** Re-exported for convenience so consumers don't need a second import for the doc name. */
export { HOUSEHOLD_DOC };
/** Key of the `Y.Map<memberId, DigestPref>` inside the `household` doc. */
export const HOUSEHOLD_DIGEST_PREFS_KEY = 'digestPrefs';
/** `HH:MM`, 24-hour, no seconds — the only shape a member's send-time preference takes. */
export const digestTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM (24h)');
/** Default per-member send time (ROADMAP §2), household-local. */
export const DEFAULT_DIGEST_TIME = '07:00';
export const digestPrefSchema = z.object({
    time: digestTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/** Parses a value out of the Yjs map; malformed/old entries fall back to the default. */
export function parseDigestPref(value) {
    const parsed = digestPrefSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
/** The effective send time for a member with no (or an invalid) stored preference. */
export function digestTimeFor(pref) {
    return pref?.time ?? DEFAULT_DIGEST_TIME;
}
/** Route the in-app digest view lives at — the push notification's deep link target. */
export const DIGEST_PATH = '/digest';
/** "Good morning, Dan" — the push title; the body is `Digest.summary`. */
export function digestPushTitle(memberName) {
    return `Good morning, ${memberName}`;
}
/** One push per member per day: redelivery replaces rather than stacks. */
export function digestPushTag(userId, dateKey) {
    return `digest:${userId}:${dateKey}`;
}
export const digestPrefUpdateRequestSchema = z.object({
    memberId: idSchema,
    time: digestTimeSchema,
});
//# sourceMappingURL=schema.js.map